<template>
  <div class="ocr-page">
    <header class="page-heading">
      <h2>OCR识别记录</h2>
      <el-button type="primary" :icon="Download" :disabled="!selected.length" :loading="exporting" @click="exportRows">导出训练数据（{{ selected.length }}）</el-button>
    </header>
    <el-form inline class="filters" @submit.prevent="search">
      <el-form-item label="VIN / 车型号"><el-input v-model="query.keyword" clearable maxlength="100" @keyup.enter="search" /></el-form-item>
      <el-form-item label="识别类型"><el-select v-model="query.ocrType" clearable><el-option label="VIN" value="VIN" /><el-option label="铭牌" value="NAMEPLATE" /></el-select></el-form-item>
      <el-form-item label="识别结果"><el-select v-model="query.success" clearable><el-option label="成功" :value="1" /><el-option label="失败" :value="0" /></el-select></el-form-item>
      <el-form-item v-for="field in fields" :key="field.key" :label="`${field.label}状态`">
        <el-select v-model="query[`${field.key}Status`]" clearable><el-option v-for="s in statuses" :key="s.value" :label="s.label" :value="s.value" /></el-select>
      </el-form-item>
      <el-form-item><el-button type="primary" :icon="Search" @click="search">查询</el-button><el-button :icon="Refresh" @click="reset">重置</el-button></el-form-item>
    </el-form>
    <el-table :data="rows" row-key="id" border v-loading="loading" @selection-change="items => selected = items.map(r => r.id)">
      <el-table-column type="selection" width="48" />
      <el-table-column prop="createTime" label="识别时间" width="170" />
      <el-table-column label="类型" width="80"><template #default="{ row }">{{ row.ocrType === 'VIN' ? 'VIN' : '铭牌' }}</template></el-table-column>
      <el-table-column label="结果" width="80"><template #default="{ row }"><el-tag :type="row.success ? 'success' : 'danger'">{{ row.success ? '成功' : '失败' }}</el-tag></template></el-table-column>
      <el-table-column v-for="f in fields" :key="f.key" :label="f.label" min-width="210">
        <template #default="{ row }">
          <div class="value">{{ row[f.original] || '--' }}</div>
          <div v-if="row[f.corrected] && row[f.corrected] !== row[f.original]" class="corrected">{{ row[f.corrected] }}</div>
          <el-tag size="small" :type="statusType(row[`${f.key}Status`])">{{ statusLabel(row[`${f.key}Status`]) }}</el-tag>
          <span v-if="ready(row, f.key)" class="ready">可导出</span>
        </template>
      </el-table-column>
      <el-table-column prop="source" label="调用入口" min-width="180" show-overflow-tooltip />
      <el-table-column prop="updateBy" label="纠错人" width="110" show-overflow-tooltip />
      <el-table-column label="操作" width="115" fixed="right"><template #default="{ row }"><el-button link type="primary" :icon="Edit" @click="open(row.id)">查看 / 纠错</el-button></template></el-table-column>
      <template #empty><el-empty description="暂无识别记录" /></template>
    </el-table>
    <el-pagination v-model:current-page="query.current" v-model:page-size="query.size" :total="total" :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next" @size-change="search" @current-change="load" />

    <el-dialog v-model="visible" title="识别详情与人工纠错" width="min(1280px, 96vw)" :close-on-click-modal="false" :close-on-press-escape="!saving" :before-close="close">
      <div v-loading="detailLoading">
        <el-alert v-if="detailError" :title="detailError" type="error" :closable="false" />
        <template v-if="record">
          <div class="review-layout">
            <section class="image-panel">
              <div class="image-toolbar">
                <el-radio-group v-model="activeField" size="small"><el-radio-button value="vin" label="vin">VIN区域</el-radio-button><el-radio-button value="model" label="model">车型号区域</el-radio-button></el-radio-group>
                <div class="image-actions">
                  <el-tooltip content="缩小"><el-button :icon="ZoomOut" :disabled="zoom <= 1" @click="zoom = Math.max(1, zoom - 0.5)" aria-label="缩小" /></el-tooltip>
                  <el-tooltip content="放大"><el-button :icon="ZoomIn" :disabled="zoom >= 4" @click="zoom = Math.min(4, zoom + 0.5)" aria-label="放大" /></el-tooltip>
                  <el-tooltip content="清除当前框选"><el-button :icon="Delete" :disabled="saving || !review[activeField].crop" @click="review[activeField].crop = null" aria-label="清除当前框选" /></el-tooltip>
                </div>
              </div>
              <div class="image-viewport" v-loading="imageLoading">
                <div v-if="imageUrl" ref="stage" class="image-stage" :style="{ width: `${zoom * 100}%` }" @pointerdown="startCrop" @pointermove="moveCrop" @pointerup="endCrop" @pointercancel="cancelCrop">
                  <img ref="sourceImage" :src="imageUrl" alt="识别原图" draggable="false" @load="imageReady" @error="imageError = '原图显示失败'" />
                  <template v-for="f in fields" :key="f.key">
                    <div v-if="review[f.key].crop && imageSize.width" class="crop-box" :class="f.key" :style="cropStyle(review[f.key].crop)"><span>{{ f.label }}</span></div>
                  </template>
                </div>
                <el-empty v-else :description="imageError || '原图加载中'" />
              </div>
            </section>
            <section class="review-fields">
              <el-alert v-if="record.errorMessage" :title="record.errorMessage" type="warning" :closable="false" />
              <el-form label-position="top" :disabled="saving">
                <div v-for="f in fields" :key="f.key" class="field-block">
                  <h3>{{ f.label }}</h3>
                  <div class="original-label">原始识别 <span>{{ record[f.original] || '--' }}</span></div>
                  <el-form-item label="人工确认值"><el-input v-model="review[f.key].value" :aria-label="`${f.label}人工确认值`" :maxlength="f.key === 'vin' ? 17 : 100" @input="changed(f)" /></el-form-item>
                  <el-form-item label="确认状态">
                    <el-select v-model="review[f.key].status" :aria-label="`${f.label}确认状态`" @change="changeStatus(f)">
                      <el-option v-for="s in statuses" :key="s.value" :label="s.label" :value="s.value" :disabled="s.value === 'CONFIRMED' && !record[f.original]" />
                    </el-select>
                  </el-form-item>
                  <div class="crop-state">{{ review[f.key].crop ? '已框选文字区域' : '未框选文字区域' }}<el-tag v-if="fieldReady(review[f.key])" size="small" type="success">可导出</el-tag></div>
                </div>
                <el-form-item label="纠错备注"><el-input v-model="review.remark" type="textarea" :rows="2" maxlength="500" show-word-limit /></el-form-item>
              </el-form>
            </section>
          </div>
          <el-collapse class="record-details">
            <el-collapse-item title="原始返回" name="raw"><pre>{{ record.rawResult || '--' }}</pre></el-collapse-item>
            <el-collapse-item title="修改历史" name="audit">
              <el-alert v-if="auditError" :title="auditError" type="warning" :closable="false" />
              <el-table :data="audits" border>
                <el-table-column prop="revision" label="版本" width="70" />
                <el-table-column prop="operator" label="操作人" width="110" />
                <el-table-column prop="createTime" label="时间" width="170" />
                <el-table-column v-for="f in fields" :key="f.key" :label="f.label" min-width="220"><template #default="{ row }">{{ auditField(row.beforeValue, f.key) }} → {{ auditField(row.afterValue, f.key) }}</template></el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>
        </template>
      </div>
      <template #footer><el-button :disabled="saving" @click="visible = false">关闭</el-button><el-button type="primary" :disabled="!record || detailLoading" :loading="saving" @click="save">保存纠错</el-button></template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Download, Search, Refresh, Edit, ZoomIn, ZoomOut, Delete } from '@element-plus/icons-vue';
import { listOcrHistory, getOcrHistory, getOcrImage, getOcrAudit, saveOcrReview, exportOcrTraining } from '@/api/ocrHistory';

const fields = [
  { key: 'vin', label: 'VIN', original: 'originalVin', corrected: 'correctedVin' },
  { key: 'model', label: '车型号', original: 'originalModel', corrected: 'correctedModel' },
];
const statuses = [
  { value: 'PENDING', label: '待确认' }, { value: 'CONFIRMED', label: '确认正确' },
  { value: 'CORRECTED', label: '已纠错' }, { value: 'UNREADABLE', label: '无法辨认' },
];
const defaults = () => ({ current: 1, size: 20, keyword: '', ocrType: '', success: '', vinStatus: '', modelStatus: '' });
const query = reactive(defaults());
const rows = ref([]), selected = ref([]), total = ref(0), loading = ref(false), exporting = ref(false);
const visible = ref(false), detailLoading = ref(false), saving = ref(false), record = ref(null), audits = ref([]);
const detailError = ref(''), auditError = ref(''), imageError = ref(''), imageLoading = ref(false), imageUrl = ref('');
const activeField = ref('vin'), zoom = ref(1), stage = ref(), sourceImage = ref();
const imageSize = reactive({ width: 0, height: 0 });
const review = reactive({ revision: 0, vin: { status: 'PENDING', value: '', crop: null }, model: { status: 'PENDING', value: '', crop: null }, remark: '' });
let loadId = 0, detailId = 0, drag = null;
const statusLabel = s => statuses.find(x => x.value === s)?.label || '待确认';
const statusType = s => ({ CONFIRMED: 'success', CORRECTED: 'warning', UNREADABLE: 'danger' }[s] || 'info');
const fieldReady = f => ['CONFIRMED', 'CORRECTED'].includes(f.status) && !!f.value && !!f.crop;
const ready = (r, key) => ['CONFIRMED', 'CORRECTED'].includes(r[`${key}Status`]) && r[`${key}Width`] > 0 && !!r[key === 'vin' ? 'correctedVin' : 'correctedModel'];
const reportError = e => { if (!e?.reported) ElMessage.error(e?.message || '操作失败'); };
const params = () => Object.fromEntries(Object.entries(query).filter(([, v]) => v !== '' && v != null));

async function load() {
  const id = ++loadId; loading.value = true;
  try { const data = await listOcrHistory(params()); if (id === loadId) { rows.value = data.records || []; total.value = data.total || 0; selected.value = []; } }
  catch (e) { if (id === loadId) reportError(e); }
  finally { if (id === loadId) loading.value = false; }
}
function search() { query.current = 1; load(); }
function reset() { Object.assign(query, defaults()); load(); }
function revokeImage() { if (imageUrl.value) URL.revokeObjectURL(imageUrl.value); imageUrl.value = ''; }
async function open(id) {
  const version = ++detailId;
  visible.value = true; detailLoading.value = true; record.value = null; audits.value = [];
  detailError.value = ''; auditError.value = ''; imageError.value = ''; revokeImage();
  zoom.value = 1; activeField.value = 'vin'; drag = null;
  imageSize.width = 0; imageSize.height = 0; imageLoading.value = false;
  try {
    const row = await getOcrHistory(id);
    if (version !== detailId) return;
    record.value = row; review.revision = row.revision; review.remark = row.remark || '';
    fields.forEach(f => {
      const crop = row[`${f.key}X`] == null ? null : { x: row[`${f.key}X`], y: row[`${f.key}Y`], width: row[`${f.key}Width`], height: row[`${f.key}Height`] };
      review[f.key] = { status: row[`${f.key}Status`], value: row[f.corrected] || row[f.original] || '', crop };
    });
    imageLoading.value = true;
    await Promise.allSettled([
      getOcrImage(id).then(blob => { if (version === detailId) imageUrl.value = URL.createObjectURL(blob); })
        .catch(e => { if (version === detailId) imageError.value = e.message || '原图加载失败'; })
        .finally(() => { if (version === detailId) imageLoading.value = false; }),
      getOcrAudit(id).then(data => { if (version === detailId) audits.value = data; })
        .catch(e => { if (version === detailId) auditError.value = e.message || '修改历史加载失败'; }),
    ]);
  } catch (e) { if (version === detailId) detailError.value = e.message || '详情加载失败'; }
  finally { if (version === detailId) detailLoading.value = false; }
}
function changed(f) {
  const item = review[f.key];
  item.status = item.value.trim().toUpperCase() === (record.value[f.original] || '').trim().toUpperCase() ? 'PENDING' : 'CORRECTED';
}
function changeStatus(f) {
  const item = review[f.key];
  if (item.status === 'CONFIRMED') item.value = record.value[f.original] || '';
  if (['PENDING', 'UNREADABLE'].includes(item.status)) item.crop = null;
  if (item.status === 'UNREADABLE') item.value = '';
}
function imageReady() { imageSize.width = sourceImage.value.naturalWidth; imageSize.height = sourceImage.value.naturalHeight; }
function point(event) {
  const bounds = stage.value.getBoundingClientRect();
  return { x: Math.round(Math.max(0, Math.min(imageSize.width, (event.clientX - bounds.left) / bounds.width * imageSize.width))),
    y: Math.round(Math.max(0, Math.min(imageSize.height, (event.clientY - bounds.top) / bounds.height * imageSize.height))) };
}
function startCrop(event) {
  if (event.button !== 0 || !imageSize.width || saving.value) return;
  event.preventDefault(); stage.value.setPointerCapture(event.pointerId);
  drag = { start: point(event), key: activeField.value, previous: review[activeField.value].crop };
}
function moveCrop(event) {
  if (!drag) return;
  const p = point(event), a = drag.start;
  review[drag.key].crop = { x: Math.min(a.x, p.x), y: Math.min(a.y, p.y), width: Math.abs(p.x - a.x), height: Math.abs(p.y - a.y) };
}
function endCrop(event) {
  if (!drag) return;
  moveCrop(event);
  const c = review[drag.key].crop;
  if (c.width < 2 || c.height < 2) review[drag.key].crop = drag.previous;
  drag = null;
}
function cancelCrop() { if (drag) review[drag.key].crop = drag.previous; drag = null; }
function cropStyle(c) {
  return { left: `${c.x / imageSize.width * 100}%`, top: `${c.y / imageSize.height * 100}%`,
    width: `${c.width / imageSize.width * 100}%`, height: `${c.height / imageSize.height * 100}%` };
}
function auditField(raw, key) {
  try { const f = JSON.parse(raw)[key]; return `${statusLabel(f.status)} · ${f.value || '--'}${f.crop ? '（已框选）' : ''}`; }
  catch { return '--'; }
}
async function save() {
  if (saving.value || !record.value) return;
  for (const f of fields) {
    const item = review[f.key];
    if (!['CONFIRMED', 'CORRECTED'].includes(item.status)) continue;
    const text = item.value.trim().toUpperCase();
    const valid = f.key === 'vin' ? /^[A-HJ-NPR-Z0-9]{17}$/.test(text) : /^[A-Z0-9][A-Z0-9._()/\-]{1,99}$/.test(text);
    if (!valid) { ElMessage.warning(f.key === 'vin' ? 'VIN必须为17位有效字母或数字' : '请填写有效车型号'); return; }
  }
  saving.value = true;
  try {
    await saveOcrReview(record.value.id, JSON.parse(JSON.stringify(review)));
    ElMessage.success('纠错已保存'); visible.value = false; await load();
  } catch (e) { reportError(e); }
  finally { saving.value = false; }
}
async function exportRows() {
  exporting.value = true;
  try {
    const blob = await exportOcrTraining(selected.value);
    const url = URL.createObjectURL(blob), link = document.createElement('a');
    link.href = url; link.download = 'ocr-training.zip'; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { reportError(e); } finally { exporting.value = false; }
}
function close(done) { if (!saving.value) done(); }
watch(visible, value => { if (!value) { detailId++; revokeImage(); drag = null; } });
onMounted(load);
onBeforeUnmount(() => { loadId++; detailId++; revokeImage(); });
</script>

<style scoped>
.ocr-page { padding: 20px; background: #fff; min-width: 0; }
.page-heading, .image-toolbar, .image-actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.page-heading { margin-bottom: 20px; } h2 { margin: 0; font-size: 20px; }
.filters { padding-bottom: 4px; } .filters :deep(.el-select) { width: 125px; } .filters :deep(.el-input) { width: 180px; }
.value, .corrected { font-family: monospace; overflow-wrap: anywhere; padding: 3px 0; }
.corrected { color: #147d64; font-weight: 600; } .ready { margin-left: 8px; font-size: 12px; color: #147d64; }
.el-pagination { margin-top: 18px; overflow-x: auto; }
.review-layout { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr); gap: 24px; }
.image-panel { min-width: 0; } .image-toolbar { margin-bottom: 12px; min-height: 32px; }
.image-actions .el-button + .el-button { margin-left: 0; }
.image-viewport { background: #f0f2f5; border: 1px solid #dcdfe6; height: 580px; overflow: auto; }
.image-stage { position: relative; user-select: none; touch-action: none; cursor: crosshair; }
.image-stage img { display: block; width: 100%; pointer-events: none; }
.crop-box { position: absolute; box-sizing: border-box; pointer-events: none; border: 2px solid #167dba; background: #167dba18; }
.crop-box.model { border-color: #ce7a19; background: #ce7a1918; }
.crop-box span { position: absolute; top: 0; left: 0; padding: 0 4px; color: #fff; font-size: 12px; background: #167dba; }
.crop-box.model span { background: #ad6411; }
.field-block { border-bottom: 1px solid #e4e7ed; padding: 0 0 14px; margin-bottom: 16px; }
h3 { font-size: 16px; margin: 4px 0 10px; } .original-label { color: #606266; margin-bottom: 14px; overflow-wrap: anywhere; }
.original-label span { display: block; color: #303133; font-family: monospace; margin-top: 6px; }
.crop-state { font-size: 13px; color: #606266; display: flex; gap: 10px; align-items: center; }
.review-fields :deep(.el-form-item) { margin-bottom: 12px; } .review-fields :deep(.el-select) { width: 100%; }
.record-details { margin-top: 20px; } pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 240px; overflow: auto; }
@media (max-width: 800px) { .review-layout { grid-template-columns: minmax(0, 1fr); } .image-viewport { height: 360px; } .ocr-page { padding: 12px; } }
</style>
